// Backend integration point: replace with API calls to product catalog and pricing service

export type DogSize = 'XS' | 'S' | 'M' | 'L' | 'XL';
export type KibbleType = 'standard' | 'premium' | 'veterinary' | 'bio' | 'grain-free';
export type AgeRange = 'puppy' | 'adult' | 'senior' | 'all-ages';
export type StockStatus = 'in-stock' | 'low-stock' | 'out-of-stock';

export interface Retailer {
  id: string;
  name: string;
  logoColor: string;
  trustBadge: boolean;
  baseUrl: string;
}

export interface PriceListing {
  id: string;
  retailerId: string;
  productId: string;
  weightKg: number;
  price: number;
  pricePerKg: number;
  shippingCost: number | null; // null = free
  stockStatus: StockStatus;
  lastUpdated: string;
  url: string;
}

export interface Product {
  id: string;
  name: string;
  brand: string;
  imageUrl: string;
  imageAlt: string;
  dogSizes: DogSize[];
  kibbleType: KibbleType;
  ageRange: AgeRange;
  mainIngredient: string;
  proteinPercent: number;
  fatPercent: number;
  description: string;
  availableWeights: number[];
  ratings: number;
  reviewCount: number;
}

export interface PriceHistory {
  date: string;
  price: number;
}

export const RETAILERS: Retailer[] = [
{ id: 'ret-zooplus', name: 'Zooplus', logoColor: '#e63946', trustBadge: true, baseUrl: 'https://www.zooplus.fr' },
{ id: 'ret-amazon', name: 'Amazon', logoColor: '#ff9900', trustBadge: true, baseUrl: 'https://www.amazon.fr' },
{ id: 'ret-animalerie', name: 'Animalerie Online', logoColor: '#2563eb', trustBadge: true, baseUrl: 'https://www.animalerie-online.com' },
{ id: 'ret-wanimo', name: 'Wanimo', logoColor: '#7c3aed', trustBadge: true, baseUrl: 'https://www.wanimo.com' },
{ id: 'ret-maxizoo', name: 'Maxi Zoo', logoColor: '#0d9488', trustBadge: true, baseUrl: 'https://www.maxizoo.fr' },
{ id: 'ret-jardiland', name: 'Jardiland', logoColor: '#16a34a', trustBadge: false, baseUrl: 'https://www.jardiland.com' },
{ id: 'ret-cdiscount', name: 'Cdiscount', logoColor: '#dc2626', trustBadge: false, baseUrl: 'https://www.cdiscount.com' },
{ id: 'ret-oscaro', name: 'Truffaut', logoColor: '#d97706', trustBadge: false, baseUrl: 'https://www.truffaut.com' }];

// ---------------------------------------------------------------------------
// AFFILIATE CONFIGURATION
// ---------------------------------------------------------------------------
// Each retailer has its own affiliate program with specific tracking parameters.
// Replace the placeholder values below with your real affiliate IDs once you
// have registered with each program.
//
// How to get your affiliate IDs:
//   • Amazon   → https://partenaires.amazon.fr  (Programme Partenaires Amazon)
//   • Zooplus  → https://www.zooplus.fr/info/affiliation  (Awin network)
//   • Wanimo   → https://www.wanimo.com/affiliation  (Effiliation / Awin)
//   • Maxi Zoo → contact via Tradedoubler or Awin
//   • Cdiscount→ https://www.cdiscount.com/affiliation  (Effiliation)
//   • Jardiland→ contact via Awin
//   • Animalerie Online → contact directly via their site
// ---------------------------------------------------------------------------

export interface AffiliateConfig {
  /** URL query parameter name used by this retailer's affiliate program */
  paramName: string;
  /** Your affiliate/partner ID — REPLACE with your real ID */
  partnerId: string;
  /** Optional extra parameters (e.g. campaign, sub-ID) */
  extraParams?: Record<string, string>;
}

/**
 * Affiliate configuration per retailer ID.
 * ⚠️  All values marked "REPLACE_ME" are placeholders — substitute your real IDs.
 */
export const AFFILIATE_CONFIG: Record<string, AffiliateConfig> = {
  // Amazon Associates (Programme Partenaires Amazon)
  // Register at: https://partenaires.amazon.fr
  'ret-amazon': {
    paramName: 'tag',
    partnerId: 'REPLACE_ME-amazon-associate-tag-21', // e.g. "croquette-21"
    extraParams: {
      linkCode: 'ogi', // Amazon standard link code — keep as-is
    },
  },

  // Zooplus — via Awin affiliate network
  // Register at: https://www.awin.com  (search "Zooplus")
  'ret-zooplus': {
    paramName: 'awc',
    partnerId: 'REPLACE_ME-zooplus-awin-id', // e.g. "12345"
    extraParams: {
      utm_source: 'affiliate',
      utm_medium: 'cpc',
      utm_campaign: 'croquette-compare',
    },
  },

  // Wanimo — via Effiliation / Awin
  // Register at: https://www.wanimo.com/affiliation
  'ret-wanimo': {
    paramName: 'affid',
    partnerId: 'REPLACE_ME-wanimo-affiliate-id', // e.g. "67890"
    extraParams: {
      utm_source: 'affiliate',
      utm_campaign: 'croquette-compare',
    },
  },

  // Maxi Zoo — via Tradedoubler or Awin
  // Register at: https://www.tradedoubler.com  (search "Maxi Zoo")
  'ret-maxizoo': {
    paramName: 'tduid',
    partnerId: 'REPLACE_ME-maxizoo-tradedoubler-id', // e.g. "p00001"
    extraParams: {
      utm_source: 'affiliate',
      utm_campaign: 'croquette-compare',
    },
  },

  // Cdiscount — via Effiliation
  // Register at: https://www.cdiscount.com/affiliation
  'ret-cdiscount': {
    paramName: 'affiliateId',
    partnerId: 'REPLACE_ME-cdiscount-affiliate-id', // e.g. "aff_123"
    extraParams: {
      utm_source: 'affiliate',
      utm_campaign: 'croquette-compare',
    },
  },

  // Jardiland — via Awin
  // Register at: https://www.awin.com  (search "Jardiland")
  'ret-jardiland': {
    paramName: 'awc',
    partnerId: 'REPLACE_ME-jardiland-awin-id', // e.g. "54321"
    extraParams: {
      utm_source: 'affiliate',
      utm_campaign: 'croquette-compare',
    },
  },

  // Animalerie Online — contact the merchant directly for their affiliate program
  'ret-animalerie': {
    paramName: 'ref',
    partnerId: 'REPLACE_ME-animalerie-partner-id', // e.g. "croquette-compare"
    extraParams: {
      utm_source: 'affiliate',
      utm_campaign: 'croquette-compare',
    },
  },

  // Truffaut — no public affiliate program yet; using UTM tracking only
  'ret-oscaro': {
    paramName: 'utm_source',
    partnerId: 'affiliate',
    extraParams: {
      utm_medium: 'referral',
      utm_campaign: 'croquette-compare',
    },
  },
};

/**
 * Builds an outbound affiliate URL by appending the retailer's tracking
 * parameters to the base product URL.
 *
 * @param baseUrl   - The raw product URL stored in PRICE_LISTINGS
 * @param retailerId - The retailer ID (e.g. "ret-amazon")
 * @returns The URL with affiliate parameters appended, or the original URL
 *          if no affiliate config exists for this retailer.
 */
export function buildAffiliateUrl(baseUrl: string, retailerId: string): string {
  const config = AFFILIATE_CONFIG[retailerId];
  if (!config) return baseUrl; // No affiliate config — return URL unchanged

  try {
    const url = new URL(baseUrl);
    // Append the primary affiliate/partner ID parameter
    url.searchParams.set(config.paramName, config.partnerId);
    // Append any extra tracking parameters
    if (config.extraParams) {
      Object.entries(config.extraParams).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });
    }
    return url.toString();
  } catch {
    // If URL parsing fails, fall back to simple query string concatenation
    const separator = baseUrl.includes('?') ? '&' : '?';
    const params = new URLSearchParams({ [config.paramName]: config.partnerId, ...config.extraParams });
    return `${baseUrl}${separator}${params.toString()}`;
  }
}


export const PRODUCTS: Product[] = [
{
  id: 'prod-rc-medium-adult',
  name: 'Royal Canin Medium Adult',
  brand: 'Royal Canin',
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1126d4709-1772729774031.png",
  imageAlt: 'Sac de croquettes Royal Canin Medium Adult pour chiens de taille moyenne',
  dogSizes: ['M'],
  kibbleType: 'premium',
  ageRange: 'adult',
  mainIngredient: 'Poulet',
  proteinPercent: 25,
  fatPercent: 13,
  description: 'Formule complète pour chiens adultes de taille moyenne (11–25 kg). Soutient la santé digestive et maintient le poids idéal grâce à une teneur adaptée en fibres.',
  availableWeights: [4, 10, 15],
  ratings: 4.6,
  reviewCount: 1284
},
{
  id: 'prod-hills-science-large',
  name: "Hill's Science Plan Large Adult",
  brand: "Hill's",
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1126d4709-1772729774031.png",
  imageAlt: "Sac de croquettes Hill's Science Plan pour grands chiens adultes",
  dogSizes: ['L', 'XL'],
  kibbleType: 'veterinary',
  ageRange: 'adult',
  mainIngredient: 'Poulet & Riz',
  proteinPercent: 28,
  fatPercent: 14,
  description: 'Nutrition cliniquement prouvée pour les grands chiens adultes. Protège les articulations et maintient une masse musculaire optimale.',
  availableWeights: [12, 18],
  ratings: 4.7,
  reviewCount: 876
},
{
  id: 'prod-purina-pro-small',
  name: 'Purina Pro Plan Small Adult',
  brand: 'Purina Pro Plan',
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1c1a70e1c-1764778436125.png",
  imageAlt: 'Sac de croquettes Purina Pro Plan Small pour petits chiens adultes',
  dogSizes: ['XS', 'S'],
  kibbleType: 'premium',
  ageRange: 'adult',
  mainIngredient: 'Saumon',
  proteinPercent: 30,
  fatPercent: 16,
  description: 'Formule riche en protéines pour les petits chiens actifs. Croquettes de petite taille adaptées à leur mâchoire.',
  availableWeights: [3, 7, 14],
  ratings: 4.5,
  reviewCount: 2103
},
{
  id: 'prod-orijen-puppy',
  name: 'Orijen Puppy Original',
  brand: 'Orijen',
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1a45fab0f-1771363534018.png",
  imageAlt: 'Sac de croquettes Orijen Puppy Original pour chiots',
  dogSizes: ['XS', 'S', 'M'],
  kibbleType: 'grain-free',
  ageRange: 'puppy',
  mainIngredient: 'Poulet & Dinde',
  proteinPercent: 38,
  fatPercent: 20,
  description: 'Croquettes biologiquement appropriées pour chiots. 85 % d\'ingrédients d\'origine animale, sans céréales, sans OGM.',
  availableWeights: [2, 6, 11.4],
  ratings: 4.8,
  reviewCount: 543
},
{
  id: 'prod-eukanuba-senior',
  name: 'Eukanuba Senior Medium',
  brand: 'Eukanuba',
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_125acca08-1777212484641.png",
  imageAlt: 'Sac de croquettes Eukanuba Senior pour chiens âgés de taille moyenne',
  dogSizes: ['M', 'L'],
  kibbleType: 'premium',
  ageRange: 'senior',
  mainIngredient: 'Poulet',
  proteinPercent: 23,
  fatPercent: 10,
  description: 'Formule adaptée aux chiens seniors (7 ans et +). Riche en glucosamine et chondroïtine pour le confort articulaire.',
  availableWeights: [5, 12],
  ratings: 4.4,
  reviewCount: 392
},
{
  id: 'prod-taste-wild-salmon',
  name: 'Taste of the Wild Pacific Stream',
  brand: 'Taste of the Wild',
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_13cc3dca9-1771363533068.png",
  imageAlt: 'Sac de croquettes Taste of the Wild Pacific Stream au saumon fumé',
  dogSizes: ['S', 'M', 'L'],
  kibbleType: 'grain-free',
  ageRange: 'all-ages',
  mainIngredient: 'Saumon fumé',
  proteinPercent: 25,
  fatPercent: 15,
  description: 'Inspiré du régime ancestral du loup. Saumon fumé comme première source de protéines, sans céréales ni colorants artificiels.',
  availableWeights: [6, 13],
  ratings: 4.6,
  reviewCount: 718
},
{
  id: 'prod-acana-light',
  name: 'Acana Light & Fit',
  brand: 'Acana',
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1c1a70e1c-1764778436125.png",
  imageAlt: 'Sac de croquettes Acana Light and Fit pour chiens en surpoids',
  dogSizes: ['M', 'L', 'XL'],
  kibbleType: 'grain-free',
  ageRange: 'adult',
  mainIngredient: 'Poulet & Dinde',
  proteinPercent: 35,
  fatPercent: 9,
  description: 'Formule hypocalorique à haute teneur en protéines pour maintenir le poids idéal sans sacrifier la masse musculaire.',
  availableWeights: [6, 11.4, 17],
  ratings: 4.5,
  reviewCount: 267
},
{
  id: 'prod-royal-canin-giant',
  name: 'Royal Canin Giant Adult',
  brand: 'Royal Canin',
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1952fe9b7-1772729775375.png",
  imageAlt: 'Sac de croquettes Royal Canin Giant Adult pour très grands chiens',
  dogSizes: ['XL'],
  kibbleType: 'premium',
  ageRange: 'adult',
  mainIngredient: 'Poulet',
  proteinPercent: 24,
  fatPercent: 12,
  description: 'Croquettes géantes spécialement formulées pour les races géantes (45 kg et +). Soutien articulaire renforcé et digestion optimisée.',
  availableWeights: [15, 25],
  ratings: 4.5,
  reviewCount: 198
},
{
  id: 'prod-brit-care-bio',
  name: 'Brit Care Grain-Free Adult',
  brand: 'Brit Care',
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_196665761-1777212487574.png",
  imageAlt: 'Sac de croquettes Brit Care Grain-Free pour chiens adultes',
  dogSizes: ['M', 'L'],
  kibbleType: 'bio',
  ageRange: 'adult',
  mainIngredient: 'Agneau & Pois',
  proteinPercent: 27,
  fatPercent: 13,
  description: 'Recette sans céréales à base d\'agneau et de pois. Idéal pour les chiens sensibles aux allergies alimentaires.',
  availableWeights: [3, 12],
  ratings: 4.3,
  reviewCount: 156
},
{
  id: 'prod-pedigree-adult',
  name: 'Pedigree Adult Complete',
  brand: 'Pedigree',
  imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_173bdb1dd-1769192527589.png",
  imageAlt: 'Sac de croquettes Pedigree Adult Complete pour chiens adultes',
  dogSizes: ['S', 'M', 'L'],
  kibbleType: 'standard',
  ageRange: 'adult',
  mainIngredient: 'Bœuf',
  proteinPercent: 21,
  fatPercent: 10,
  description: 'Alimentation complète et équilibrée au bœuf pour chiens adultes. Enrichie en vitamines et minéraux essentiels.',
  availableWeights: [3, 10, 20],
  ratings: 4.1,
  reviewCount: 3421
}];


// Price listings — Backend integration point: replace with live pricing API
export const PRICE_LISTINGS: PriceListing[] = [
// Royal Canin Medium Adult — 10kg
{ id: 'pl-rc-med-zoo-10', retailerId: 'ret-zooplus', productId: 'prod-rc-medium-adult', weightKg: 10, price: 52.99, pricePerKg: 5.30, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.zooplus.fr' },
{ id: 'pl-rc-med-amz-10', retailerId: 'ret-amazon', productId: 'prod-rc-medium-adult', weightKg: 10, price: 54.90, pricePerKg: 5.49, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.amazon.fr' },
{ id: 'pl-rc-med-ani-10', retailerId: 'ret-animalerie', productId: 'prod-rc-medium-adult', weightKg: 10, price: 56.50, pricePerKg: 5.65, shippingCost: 4.90, stockStatus: 'in-stock', lastUpdated: '2026-04-25', url: 'https://www.animalerie-online.com' },
{ id: 'pl-rc-med-wan-10', retailerId: 'ret-wanimo', productId: 'prod-rc-medium-adult', weightKg: 10, price: 58.00, pricePerKg: 5.80, shippingCost: null, stockStatus: 'low-stock', lastUpdated: '2026-04-26', url: 'https://www.wanimo.com' },
{ id: 'pl-rc-med-max-10', retailerId: 'ret-maxizoo', productId: 'prod-rc-medium-adult', weightKg: 10, price: 59.99, pricePerKg: 6.00, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-24', url: 'https://www.maxizoo.fr' },
{ id: 'pl-rc-med-jar-10', retailerId: 'ret-jardiland', productId: 'prod-rc-medium-adult', weightKg: 10, price: 62.00, pricePerKg: 6.20, shippingCost: 5.90, stockStatus: 'in-stock', lastUpdated: '2026-04-23', url: 'https://www.jardiland.com' },
{ id: 'pl-rc-med-cdi-10', retailerId: 'ret-cdiscount', productId: 'prod-rc-medium-adult', weightKg: 10, price: 55.49, pricePerKg: 5.55, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.cdiscount.com' },
{ id: 'pl-rc-med-tru-10', retailerId: 'ret-oscaro', productId: 'prod-rc-medium-adult', weightKg: 10, price: 63.50, pricePerKg: 6.35, shippingCost: 4.50, stockStatus: 'out-of-stock', lastUpdated: '2026-04-22', url: 'https://www.truffaut.com' },
// Royal Canin Medium Adult — 4kg
{ id: 'pl-rc-med-zoo-4', retailerId: 'ret-zooplus', productId: 'prod-rc-medium-adult', weightKg: 4, price: 23.99, pricePerKg: 6.00, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.zooplus.fr' },
{ id: 'pl-rc-med-amz-4', retailerId: 'ret-amazon', productId: 'prod-rc-medium-adult', weightKg: 4, price: 24.90, pricePerKg: 6.23, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.amazon.fr' },
{ id: 'pl-rc-med-wan-4', retailerId: 'ret-wanimo', productId: 'prod-rc-medium-adult', weightKg: 4, price: 25.50, pricePerKg: 6.38, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.wanimo.com' },
// Royal Canin Medium Adult — 15kg
{ id: 'pl-rc-med-zoo-15', retailerId: 'ret-zooplus', productId: 'prod-rc-medium-adult', weightKg: 15, price: 74.99, pricePerKg: 5.00, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.zooplus.fr' },
{ id: 'pl-rc-med-amz-15', retailerId: 'ret-amazon', productId: 'prod-rc-medium-adult', weightKg: 15, price: 77.90, pricePerKg: 5.19, shippingCost: null, stockStatus: 'low-stock', lastUpdated: '2026-04-26', url: 'https://www.amazon.fr' },

// Hills Large Adult — 12kg
{ id: 'pl-hi-lg-zoo-12', retailerId: 'ret-zooplus', productId: 'prod-hills-science-large', weightKg: 12, price: 68.99, pricePerKg: 5.75, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.zooplus.fr' },
{ id: 'pl-hi-lg-amz-12', retailerId: 'ret-amazon', productId: 'prod-hills-science-large', weightKg: 12, price: 71.50, pricePerKg: 5.96, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.amazon.fr' },
{ id: 'pl-hi-lg-wan-12', retailerId: 'ret-wanimo', productId: 'prod-hills-science-large', weightKg: 12, price: 73.00, pricePerKg: 6.08, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-25', url: 'https://www.wanimo.com' },
{ id: 'pl-hi-lg-max-12', retailerId: 'ret-maxizoo', productId: 'prod-hills-science-large', weightKg: 12, price: 75.99, pricePerKg: 6.33, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-24', url: 'https://www.maxizoo.fr' },
{ id: 'pl-hi-lg-jar-12', retailerId: 'ret-jardiland', productId: 'prod-hills-science-large', weightKg: 12, price: 78.00, pricePerKg: 6.50, shippingCost: 5.90, stockStatus: 'low-stock', lastUpdated: '2026-04-23', url: 'https://www.jardiland.com' },

// Purina Pro Plan Small — 7kg
{ id: 'pl-pp-sm-zoo-7', retailerId: 'ret-zooplus', productId: 'prod-purina-pro-small', weightKg: 7, price: 38.50, pricePerKg: 5.50, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.zooplus.fr' },
{ id: 'pl-pp-sm-amz-7', retailerId: 'ret-amazon', productId: 'prod-purina-pro-small', weightKg: 7, price: 39.90, pricePerKg: 5.70, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.amazon.fr' },
{ id: 'pl-pp-sm-ani-7', retailerId: 'ret-animalerie', productId: 'prod-purina-pro-small', weightKg: 7, price: 41.00, pricePerKg: 5.86, shippingCost: 4.90, stockStatus: 'in-stock', lastUpdated: '2026-04-25', url: 'https://www.animalerie-online.com' },
{ id: 'pl-pp-sm-cdi-7', retailerId: 'ret-cdiscount', productId: 'prod-purina-pro-small', weightKg: 7, price: 40.49, pricePerKg: 5.78, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.cdiscount.com' },

// Orijen Puppy — 6kg
{ id: 'pl-or-pu-zoo-6', retailerId: 'ret-zooplus', productId: 'prod-orijen-puppy', weightKg: 6, price: 62.99, pricePerKg: 10.50, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.zooplus.fr' },
{ id: 'pl-or-pu-amz-6', retailerId: 'ret-amazon', productId: 'prod-orijen-puppy', weightKg: 6, price: 64.90, pricePerKg: 10.82, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.amazon.fr' },
{ id: 'pl-or-pu-wan-6', retailerId: 'ret-wanimo', productId: 'prod-orijen-puppy', weightKg: 6, price: 66.00, pricePerKg: 11.00, shippingCost: null, stockStatus: 'low-stock', lastUpdated: '2026-04-25', url: 'https://www.wanimo.com' },

// Pedigree Adult — 10kg
{ id: 'pl-pd-ad-zoo-10', retailerId: 'ret-zooplus', productId: 'prod-pedigree-adult', weightKg: 10, price: 19.99, pricePerKg: 2.00, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.zooplus.fr' },
{ id: 'pl-pd-ad-amz-10', retailerId: 'ret-amazon', productId: 'prod-pedigree-adult', weightKg: 10, price: 21.50, pricePerKg: 2.15, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.amazon.fr' },
{ id: 'pl-pd-ad-cdi-10', retailerId: 'ret-cdiscount', productId: 'prod-pedigree-adult', weightKg: 10, price: 20.49, pricePerKg: 2.05, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.cdiscount.com' },
{ id: 'pl-pd-ad-jar-10', retailerId: 'ret-jardiland', productId: 'prod-pedigree-adult', weightKg: 10, price: 22.90, pricePerKg: 2.29, shippingCost: 5.90, stockStatus: 'in-stock', lastUpdated: '2026-04-24', url: 'https://www.jardiland.com' },
{ id: 'pl-pd-ad-max-10', retailerId: 'ret-maxizoo', productId: 'prod-pedigree-adult', weightKg: 10, price: 23.50, pricePerKg: 2.35, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-25', url: 'https://www.maxizoo.fr' },

// Taste of the Wild — 13kg
{ id: 'pl-tw-sal-zoo-13', retailerId: 'ret-zooplus', productId: 'prod-taste-wild-salmon', weightKg: 13, price: 54.99, pricePerKg: 4.23, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.zooplus.fr' },
{ id: 'pl-tw-sal-amz-13', retailerId: 'ret-amazon', productId: 'prod-taste-wild-salmon', weightKg: 13, price: 57.00, pricePerKg: 4.38, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.amazon.fr' },
{ id: 'pl-tw-sal-ani-13', retailerId: 'ret-animalerie', productId: 'prod-taste-wild-salmon', weightKg: 13, price: 58.50, pricePerKg: 4.50, shippingCost: 4.90, stockStatus: 'low-stock', lastUpdated: '2026-04-25', url: 'https://www.animalerie-online.com' },

// Brit Care — 12kg
{ id: 'pl-bc-gf-zoo-12', retailerId: 'ret-zooplus', productId: 'prod-brit-care-bio', weightKg: 12, price: 44.99, pricePerKg: 3.75, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.zooplus.fr' },
{ id: 'pl-bc-gf-amz-12', retailerId: 'ret-amazon', productId: 'prod-brit-care-bio', weightKg: 12, price: 46.50, pricePerKg: 3.88, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.amazon.fr' },
{ id: 'pl-bc-gf-wan-12', retailerId: 'ret-wanimo', productId: 'prod-brit-care-bio', weightKg: 12, price: 47.90, pricePerKg: 3.99, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-25', url: 'https://www.wanimo.com' },

// Acana Light — 11.4kg
{ id: 'pl-ac-lf-zoo-11', retailerId: 'ret-zooplus', productId: 'prod-acana-light', weightKg: 11.4, price: 71.99, pricePerKg: 6.32, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.zooplus.fr' },
{ id: 'pl-ac-lf-amz-11', retailerId: 'ret-amazon', productId: 'prod-acana-light', weightKg: 11.4, price: 74.00, pricePerKg: 6.49, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.amazon.fr' },
{ id: 'pl-ac-lf-wan-11', retailerId: 'ret-wanimo', productId: 'prod-acana-light', weightKg: 11.4, price: 75.50, pricePerKg: 6.62, shippingCost: null, stockStatus: 'low-stock', lastUpdated: '2026-04-25', url: 'https://www.wanimo.com' },

// Eukanuba Senior — 12kg
{ id: 'pl-eu-sr-zoo-12', retailerId: 'ret-zooplus', productId: 'prod-eukanuba-senior', weightKg: 12, price: 46.99, pricePerKg: 3.92, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.zooplus.fr' },
{ id: 'pl-eu-sr-amz-12', retailerId: 'ret-amazon', productId: 'prod-eukanuba-senior', weightKg: 12, price: 48.50, pricePerKg: 4.04, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.amazon.fr' },
{ id: 'pl-eu-sr-max-12', retailerId: 'ret-maxizoo', productId: 'prod-eukanuba-senior', weightKg: 12, price: 50.00, pricePerKg: 4.17, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-25', url: 'https://www.maxizoo.fr' },

// Royal Canin Giant — 15kg
{ id: 'pl-rc-gi-zoo-15', retailerId: 'ret-zooplus', productId: 'prod-royal-canin-giant', weightKg: 15, price: 64.99, pricePerKg: 4.33, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.zooplus.fr' },
{ id: 'pl-rc-gi-amz-15', retailerId: 'ret-amazon', productId: 'prod-royal-canin-giant', weightKg: 15, price: 67.50, pricePerKg: 4.50, shippingCost: null, stockStatus: 'in-stock', lastUpdated: '2026-04-26', url: 'https://www.amazon.fr' },
{ id: 'pl-rc-gi-wan-15', retailerId: 'ret-wanimo', productId: 'prod-royal-canin-giant', weightKg: 15, price: 69.00, pricePerKg: 4.60, shippingCost: null, stockStatus: 'low-stock', lastUpdated: '2026-04-25', url: 'https://www.wanimo.com' }];


// Price history for Royal Canin Medium Adult 10kg — Backend integration point
export const PRICE_HISTORY_RC_MEDIUM: PriceHistory[] = [
{ date: '28 jan', price: 58.50 },
{ date: '04 fév', price: 57.20 },
{ date: '11 fév', price: 57.20 },
{ date: '18 fév', price: 55.99 },
{ date: '25 fév', price: 54.50 },
{ date: '04 mar', price: 56.00 },
{ date: '11 mar', price: 59.90 },
{ date: '18 mar', price: 61.00 },
{ date: '25 mar', price: 58.00 },
{ date: '01 avr', price: 55.49 },
{ date: '08 avr', price: 53.99 },
{ date: '15 avr', price: 52.99 },
{ date: '22 avr', price: 52.99 },
{ date: '26 avr', price: 52.99 }];


export function getBestPrice(productId: string, weightKg?: number): PriceListing | null {
  const listings = PRICE_LISTINGS.filter(
    (l) => l.productId === productId && l.stockStatus !== 'out-of-stock' && (weightKg ? l.weightKg === weightKg : true)
  );
  if (!listings.length) return null;
  return listings.reduce((best, l) => l.price < best.price ? l : best);
}

export function getRetailerById(id: string): Retailer | undefined {
  return RETAILERS.find((r) => r.id === id);
}

export function getListingsForProduct(productId: string, weightKg?: number): PriceListing[] {
  return PRICE_LISTINGS.filter(
    (l) => l.productId === productId && (weightKg ? l.weightKg === weightKg : true)
  ).sort((a, b) => a.price - b.price);
}

export function getProductById(id: string): Product | undefined {
  return PRODUCTS.find((p) => p.id === id);
}

export const DOG_SIZE_LABELS: Record<DogSize, string> = {
  XS: 'Très petit (< 4 kg)',
  S: 'Petit (4–10 kg)',
  M: 'Moyen (11–25 kg)',
  L: 'Grand (26–44 kg)',
  XL: 'Très grand (45 kg+)'
};

export const KIBBLE_TYPE_LABELS: Record<KibbleType, string> = {
  standard: 'Standard',
  premium: 'Premium',
  veterinary: 'Vétérinaire',
  bio: 'Bio / Naturel',
  'grain-free': 'Sans céréales'
};

export const AGE_RANGE_LABELS: Record<AgeRange, string> = {
  puppy: 'Chiot',
  adult: 'Adulte',
  senior: 'Senior',
  'all-ages': 'Tous âges'
};